import { useCallback, useRef, useState } from "react";
import type { FC } from "react";
import Select from "react-select";
import type { SelectInstance } from "react-select";

import type { IOption } from "store/slices/topicPractice";

import { MIN_SPACE_FOR_TOP_MENU, useFixedMenu } from "../useFixedMenu";

interface Props {
  index: number;
  placeholder: string;
  options: IOption[];
  isLast: boolean;
  selected?: string;
  onChange: (index: number, value: string) => void;
}

interface SelectOption {
  value: string;
  label: string;
}

const Option: FC<Props> = ({ index, placeholder, options, isLast, selected, onChange }) => {
  const selectRef = useRef<SelectInstance<SelectOption>>(null);
  const [lastPlacement, setLastPlacement] = useState<"top" | "bottom">("top");
  const menuPlacement = isLast ? lastPlacement : "bottom";

  // AIDEV-NOTE: Poslední výběr bývá u dolního okraje, proto chce nabídku nahoru. Když je ale nad ovladačem méně než
  // `MIN_SPACE_FOR_TOP_MENU` (poslední výběr nascrollovaný k horní hraně), `top` větev react-selectu nabídku přehodí
  // dolů BEZ omezení výšky a spodní možnosti jsou nedosažitelné. Stranu proto určíme při otevření sami; `bottom` pak
  // výšku omezí. Při méně místa na obou stranách (viewport pod ~320 px) pomůže jen fallback react-selectu. [TD-485]
  const choosePlacement = useCallback(() => {
    if (!isLast) return;
    const control = selectRef.current?.controlRef;
    if (!control) return;
    setLastPlacement(control.getBoundingClientRect().top >= MIN_SPACE_FOR_TOP_MENU ? "top" : "bottom");
  }, [isLast]);
  const fixedMenu = useFixedMenu(choosePlacement);

  return (
    <Select
      {...fixedMenu}
      ref={selectRef}
      name="subject"
      className={`react-select-container react-select--${menuPlacement}`}
      classNamePrefix="react-select"
      placeholder={placeholder}
      isSearchable={false}
      menuPlacement={menuPlacement}
      options={options.map(option => ({
        value: option.value ?? "",
        label: option.value ?? "",
      }))}
      onChange={e => {
        onChange(index, e?.value ?? "");
      }}
      value={selected ? { value: selected, label: selected } : null}
    />
  );
};

export default Option;
